import { useMemo } from 'react';
import { useSelector } from 'react-redux';

import { CatalogItemInOrder } from 'entities/CatalogItem/model/types/CatalogItemSchema';
import {
    getSelectedOrder,
    getShipmentStatus,
} from 'entities/Order/model/selectors/ordersSelectors';
import { Order, ShipmentSchema } from 'entities/Order/model/types/OrderSchema';
import { SelectedProposal } from 'entities/Proposal/model/types/ProposalSchema';
import { getPropertyVendorsMap } from 'entities/Vendors/model/selectors/vendorsSelectors';
import { Vendor } from 'entities/Vendors/model/types/VendorsSchema';

export interface SupplierInfo {
    name: string;
    logo?: string;
    sellerUid: string;
    accountNumber?: string;
    phone?: string;
    email?: string;
    primaryContact?: string;
    primaryPhone?: string;
    primaryEmail?: string;
    shipments?: Array<{
        order?: string;
        shipped?: string;
        carrier?: string;
    }>;
}

const getSupplierDetails = (
    vendor: Vendor,
    shipments: ShipmentSchema[],
    orderNumber: string,
): SupplierInfo => ({
    name: vendor.title,
    logo: vendor?.logo ?? '',
    sellerUid: vendor.externalUuid,
    accountNumber: vendor?.operationsAccountNumber ?? '',
    phone: vendor?.vendorPhone ?? '',
    email: vendor?.vendorEmail ?? '',
    primaryContact: vendor?.primaryContact ?? '',
    primaryPhone: vendor?.primaryContactPhone ?? '',
    primaryEmail: vendor?.primaryContactEmail ?? '',
    shipments: shipments?.length
        ? shipments.map((shipment) => ({
              order: shipment.shipmentId || orderNumber,
              shipped: shipment.trackingInfo?.createdAt,
              carrier: shipment.trackingInfo?.carrier,
          }))
        : [],
});

const getShipmentsForSupplier = (
    shipmentStatus: ShipmentSchema[] | null,
    sellerUid: string,
    proposalName: string,
): ShipmentSchema[] => {
    if (!Array.isArray(shipmentStatus)) return [];

    return shipmentStatus.filter(
        (shipment: ShipmentSchema) =>
            shipment.supplierUid === sellerUid ||
            shipment.seller === proposalName,
    );
};

const collectUniqueSuppliers = (
    selectedOrder: Order,
    vendorsMap: Record<string, Vendor>,
    shipmentStatus: ShipmentSchema[] | null,
): Map<string, SupplierInfo> => {
    const suppliersMap = new Map<string, SupplierInfo>();

    const proposals = selectedOrder.items.flatMap(
        (item: CatalogItemInOrder) => item.selectedProposals,
    );

    proposals.forEach((proposal: SelectedProposal) => {
        const sellerUid = proposal.sellerUid;

        if (suppliersMap.has(sellerUid)) return;

        const vendor = vendorsMap[sellerUid];
        const shipments = getShipmentsForSupplier(
            shipmentStatus,
            sellerUid,
            proposal.name,
        );

        const supplierDetails = getSupplierDetails(
            vendor,
            shipments,
            selectedOrder.orderNumber,
        );

        suppliersMap.set(sellerUid, supplierDetails);
    });

    return suppliersMap;
};

export const useSuppliers = (): SupplierInfo[] => {
    const selectedOrder = useSelector(getSelectedOrder);
    const vendorsMap = useSelector(getPropertyVendorsMap);
    const shipmentStatus = useSelector(getShipmentStatus);

    return useMemo((): SupplierInfo[] => {
        if (!selectedOrder?.items || !vendorsMap) return [];

        const suppliersMap = collectUniqueSuppliers(
            selectedOrder,
            vendorsMap,
            shipmentStatus,
        );

        return Array.from(suppliersMap.values()).sort((a, b) =>
            a.name.localeCompare(b.name),
        );
    }, [selectedOrder, vendorsMap, shipmentStatus]);
};
